import { useEffect, useState } from "react"
import { ControlGroup } from "../ControlGroup"
import { SharedMemoryCounters } from "@/utils/measurement/memory-counters"
import clsx from "clsx"

interface MeasurementLabel {
	name: string
	categories: string[]
	size: number
}

export const MemoryUtilisationPanel = () => {
	const [labels, setLabels] = useState<MeasurementLabel[]>([])
	const [totalCPU, setTotalCPU] = useState(0)
	const [totalGPU, setTotalGPU] = useState(0)

	useEffect(() => {
		const interval = setInterval(updateUsages, 1500)

		return () => {
			clearInterval(interval)
		}
	}, [])

	const updateUsages = async () => {
		const newLabels: MeasurementLabel[] = []
		let totalCPU = 0
		let totalGPU = 0

		for await (const [label, counter] of SharedMemoryCounters.allCounters) {
			const size = await counter.handler()
			const categories = counter.categories

			newLabels.push({
				name: label,
				categories,
				size,
			})

			if (counter.categories.includes("gpu")) {
				totalGPU += size
			} else {
				totalCPU += size
			}
		}

		newLabels.sort((a, b) => {
			return b.size - a.size
		})

		setLabels(newLabels)
		setTotalCPU(totalCPU)
		setTotalGPU(totalGPU)
	}

	const formatByteLength = (value: number) => {
		return `${(value / 1024 / 1024).toFixed(2)}`
	}

	const renderLabel = (label: MeasurementLabel) => {
		const getCategoryColor = (category: string) => {
			switch (category) {
				case "cpu":
					return "bg-lime-900"

				case "gpu":
					return "bg-sky-900"

				case "ai-thread":
					return "bg-rose-900"

				case "main-thread":
					return "bg-amber-900"

				case "renderer":
					return "bg-violet-900"

				default:
					return "bg-base-900"
			}
		}

		return (
			<div className="flex w-full items-center justify-between rounded-sm bg-base-600/25 p-2">
				<div>
					<span className="mb-1 block font-bold">{label.name}</span>
					<div className="flow space-x-2">
						{label.categories.map((category) => {
							return (
								<span
									key={category}
									className={clsx(
										"inline-block rounded px-1 py-0.5 text-xxxs font-bold uppercase",
										getCategoryColor(category),
									)}
								>
									{category}
								</span>
							)
						})}
					</div>
				</div>
				<div className="text-small font- font-mono font-medium">
					{formatByteLength(label.size)}MB
				</div>
			</div>
		)
	}

	return (
		<ControlGroup name="Memory Utilization" expandKey="measurement" isExpanded isExperimental>
			<div className="flex flex-col space-y-2 pt-2 text-xs">
				<div className="flex space-x-2">
					{renderLabel({
						name: "RAM",
						size: totalCPU,
						categories: ["cpu"],
					})}
					{renderLabel({
						name: "VRAM",
						size: totalGPU,
						categories: ["gpu"],
					})}
				</div>
				{labels.map((label) => (
					<div key={label.name}>{renderLabel(label)}</div>
				))}
			</div>
		</ControlGroup>
	)
}
